// ── ImportProgressOverlay ────────────────────────────────────────────────────
// Deterministic phased progress overlay for long-running scope imports.
//
// Since the import pipeline (Browser → BFF → Go → Postgres) is a single
// synchronous HTTP call, there is no server-push channel for real progress.
// Instead we estimate duration from the manifest metrics (scope count,
// outcome count) using empirical per-item rates and render a smooth
// animated overlay with phase transitions, elapsed/remaining time, and
// contextual descriptions — similar to Vercel/Linear deploy screens.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEscapeLayer } from '@/shared/hooks/useEscapeLayer';
import {
  CheckCircle2,
  Database,
  FileStack,
  Layers,
  Loader2,
  PackageCheck,
  Send,
  XCircle,
} from 'lucide-react';

import type { ImportResult, ScopeManifest } from '../types';
import type { ConflictStrategy } from '../../../api/scopeMigrateApi';

// ── Phase model ─────────────────────────────────────────────────────────────

interface Phase {
  key: string;
  label: string;
  description: string;
  /** Fraction of *total estimated time* this phase occupies (0-1). */
  weight: number;
  icon: React.ElementType;
}

function buildPhases(scopeCount: number, outcomeCount: number): Phase[] {
  // Weights are proportional to expected wall-clock time.
  // Outcome insertion dominates (one INSERT + trigger per row).
  const hasOutcomes = outcomeCount > 0;
  return [
    {
      key: 'sending',
      label: 'Uploading manifest',
      description: 'Sending JSON payload to the orchestrator…',
      weight: 0.04,
      icon: Send,
    },
    {
      key: 'scopes',
      label: 'Upserting scopes',
      description: `Creating or verifying ${scopeCount} scope${scopeCount === 1 ? '' : 's'}…`,
      weight: 0.1,
      icon: Layers,
    },
    ...(hasOutcomes
      ? [
          {
            key: 'definitions',
            label: 'Registering definitions',
            description: 'Auto-registering missing outcome definitions…',
            weight: 0.06,
            icon: FileStack,
          },
          {
            key: 'outcomes',
            label: 'Writing outcomes',
            description: `Inserting ${outcomeCount.toLocaleString()} outcome${outcomeCount === 1 ? '' : 's'} with trigger validation…`,
            weight: 0.68,
            icon: Database,
          },
        ]
      : []),
    {
      key: 'committing',
      label: 'Committing transaction',
      description: 'Finalizing database transaction…',
      weight: hasOutcomes ? 0.12 : 0.86,
      icon: PackageCheck,
    },
  ];
}

// ── Duration estimation ─────────────────────────────────────────────────────
// Empirical rates measured on Cloud Run (2025-06 calibration):
//   Benchmark: 2 402 scopes + 271 outcomes completed in ~6 s (fresh DB).
//   Upsert-on-existing adds probing + possible DELETE + re-INSERT overhead,
//   so the per-item rates below are set ~2× above the fresh-insert floor
//   to remain safe for both the "fresh" and "upsert" paths.
//
//   - scope upsert:        ~3 ms / scope
//   - outcome insert:      ~5 ms / outcome (includes trigger)
//   - definition register: ~3 ms / unique handler
//   - base overhead:       ~1.5 s (TCP, JSON parse, tx begin/commit)
function estimateTotalMs(scopeCount: number, outcomeCount: number): number {
  const BASE = 1_500;
  const scopeMs = scopeCount * 3;
  const outcomeMs = outcomeCount * 5;
  // Rough handler count (most manifests use 1 handler per scope)
  const defMs = Math.min(scopeCount, 20) * 3;
  return Math.max(3_000, BASE + scopeMs + outcomeMs + defMs);
}

// ── Time formatting helpers ─────────────────────────────────────────────────

function formatElapsed(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return min > 0 ? `${min}m ${sec}s` : `${sec}s`;
}

function formatRemaining(ms: number): string {
  if (ms <= 0) return 'almost done';
  const totalSec = Math.ceil(ms / 1000);
  if (totalSec <= 5) return 'a few seconds';
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  if (min > 0) return `~${min}m ${sec}s`;
  return `~${sec}s`;
}

// ── Component ───────────────────────────────────────────────────────────────

export interface ImportProgressOverlayProps {
  /** True while the import HTTP call is in flight. */
  isActive: boolean;
  /** Manifest being imported (for scope/outcome counts). */
  manifest: ScopeManifest;
  /** Conflict strategy being used. */
  strategy: ConflictStrategy;
  /** Target tenant display name. */
  targetTenantLabel: string;
  /** Set once the import call resolves successfully. */
  result: ImportResult | null;
  /** Set if the import call rejects. */
  error: string | null;
  /** Called when the user dismisses the overlay (only available after done/error). */
  onDismiss: () => void;
}

export function ImportProgressOverlay({
  isActive,
  manifest,
  strategy,
  targetTenantLabel,
  result,
  error,
  onDismiss,
}: ImportProgressOverlayProps) {
  useEscapeLayer(onDismiss, !isActive && (!!result || !!error));

  const scopeCount = manifest.scopes.length;
  const outcomeCount = manifest.outcomes?.length ?? 0;

  const phases = useMemo(
    () => buildPhases(scopeCount, outcomeCount),
    [scopeCount, outcomeCount],
  );

  const totalEstimatedMs = useMemo(
    () => estimateTotalMs(scopeCount, outcomeCount),
    [scopeCount, outcomeCount],
  );

  // ── Timer state ─────────────────────────────────────────────────────
  const startRef = useRef<number>(0);
  const rafRef = useRef<number>(0);
  const [elapsedMs, setElapsedMs] = useState(0);

  // Smooth progress value [0..1]. We deliberately slow down as it approaches
  // the cap (0.95) to give a feeling of "almost there" without faking 100%.
  const [progress, setProgress] = useState(0);

  // Start / stop the animation loop.
  const tick = useCallback(() => {
    const now = performance.now();
    const elapsed = now - startRef.current;
    setElapsedMs(elapsed);

    // Asymptotic progress: fast start → slow approach to 0.95.
    // Uses 1 − e^(−t/T) which organically decelerates.
    const fraction = 1 - Math.exp(-elapsed / totalEstimatedMs);
    // Cap at 0.95 so we never show 100% until the server responds.
    setProgress(Math.min(fraction, 0.95));

    rafRef.current = requestAnimationFrame(tick);
  }, [totalEstimatedMs]);

  useEffect(() => {
    if (isActive && !result && !error) {
      startRef.current = performance.now();
      rafRef.current = requestAnimationFrame(tick);
    }
    return () => cancelAnimationFrame(rafRef.current);
  }, [isActive, result, error, tick]);

  // When result arrives → snap to 100%.
  useEffect(() => {
    if (result) {
      cancelAnimationFrame(rafRef.current);
      // Animate from current → 100% over 400ms.
      const from = progress;
      const startSnap = performance.now();
      const snapDuration = 400;
      const animate = () => {
        const t = Math.min((performance.now() - startSnap) / snapDuration, 1);
        // ease-out cubic
        const eased = 1 - Math.pow(1 - t, 3);
        setProgress(from + (1 - from) * eased);
        if (t < 1) rafRef.current = requestAnimationFrame(animate);
      };
      rafRef.current = requestAnimationFrame(animate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result]);

  // When error arrives → freeze progress.
  useEffect(() => {
    if (error) cancelAnimationFrame(rafRef.current);
  }, [error]);

  // ── Derived state ───────────────────────────────────────────────────

  const pct = Math.round(progress * 100);
  const isDone = !!result;
  const isFailed = !!error;

  // Determine active phase based on progress.
  const activePhaseIdx = useMemo(() => {
    if (isDone) return phases.length; // past all phases
    let cumWeight = 0;
    for (let i = 0; i < phases.length; i++) {
      cumWeight += phases[i].weight;
      if (progress < cumWeight) return i;
    }
    return phases.length - 1;
  }, [progress, phases, isDone]);

  const remainingMs = Math.max(0, totalEstimatedMs - elapsedMs);

  // Don't render when nothing is happening.
  if (!isActive && !result && !error) return null;

  return (
    <div className="absolute inset-0 bg-black/85 backdrop-blur-md z-30 flex items-center justify-center p-4">
      <div className="pac-glass-raised rounded-xl p-6 max-w-md w-full shadow-2xl space-y-5">
        {/* ── Header ─────────────────────────────────────────────── */}
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <h3 className="text-sm font-semibold text-neutral-100 flex items-center gap-2">
              {isDone ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              ) : isFailed ? (
                <XCircle className="w-4 h-4 text-red-400" />
              ) : (
                <Loader2 className="w-4 h-4 text-violet-400 animate-spin" />
              )}
              {isDone
                ? 'Import Complete'
                : isFailed
                  ? 'Import Failed'
                  : 'Importing…'}
            </h3>
            <p className="text-[10px] text-neutral-500">
              {strategy} → {targetTenantLabel}
            </p>
          </div>

          {/* Elapsed / remaining */}
          <div className="text-right space-y-0.5">
            <div className="text-xs tabular-nums text-neutral-300">
              {formatElapsed(elapsedMs)}
            </div>
            {!isDone && !isFailed && (
              <div className="text-[10px] tabular-nums text-neutral-600">
                {formatRemaining(remainingMs)} left
              </div>
            )}
          </div>
        </div>

        {/* ── Progress bar ───────────────────────────────────────── */}
        <div className="space-y-1.5">
          <div className="h-2 rounded-full bg-neutral-800 overflow-hidden relative">
            <div
              className={
                'h-full rounded-full transition-[width] duration-300 ease-out ' +
                (isFailed
                  ? 'bg-red-500'
                  : isDone
                    ? 'bg-emerald-500'
                    : 'bg-gradient-to-r from-violet-600 via-violet-500 to-fuchsia-500')
              }
              style={{ width: `${pct}%` }}
            />
            {/* Shimmer overlay while active */}
            {!isDone && !isFailed && (
              <div className="absolute inset-0 overflow-hidden rounded-full">
                <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/10 to-transparent" />
              </div>
            )}
          </div>
          <div className="flex items-center justify-between text-[10px]">
            <span className="text-neutral-500">
              {scopeCount} scope{scopeCount !== 1 ? 's' : ''}
              {outcomeCount > 0 &&
                ` · ${outcomeCount.toLocaleString()} outcome${outcomeCount !== 1 ? 's' : ''}`}
            </span>
            <span
              className={
                'tabular-nums font-medium ' +
                (isFailed
                  ? 'text-red-400'
                  : isDone
                    ? 'text-emerald-400'
                    : 'text-violet-400')
              }
            >
              {pct}%
            </span>
          </div>
        </div>

        {/* ── Phase list ─────────────────────────────────────────── */}
        <div className="space-y-0.5">
          {phases.map((phase, idx) => {
            const isCompleted = idx < activePhaseIdx;
            const isCurrent = idx === activePhaseIdx && !isDone && !isFailed;
            const Icon = phase.icon;
            return (
              <div
                key={phase.key}
                className={
                  'flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-xs transition-all duration-300 ' +
                  (isCurrent
                    ? 'pac-glass-subtle border border-neutral-700/40'
                    : isCompleted
                      ? 'opacity-60'
                      : 'opacity-30')
                }
              >
                <div className="shrink-0 w-5 h-5 flex items-center justify-center">
                  {isCompleted ? (
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                  ) : isCurrent ? (
                    <Icon className="w-3.5 h-3.5 text-violet-400 animate-pulse" />
                  ) : (
                    <Icon className="w-3.5 h-3.5 text-neutral-600" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div
                    className={
                      'font-medium truncate ' +
                      (isCurrent
                        ? 'text-neutral-200'
                        : isCompleted
                          ? 'text-neutral-400'
                          : 'text-neutral-600')
                    }
                  >
                    {phase.label}
                  </div>
                  {isCurrent && (
                    <div className="text-[10px] text-neutral-500 truncate mt-0.5">
                      {phase.description}
                    </div>
                  )}
                </div>
                {isCompleted && (
                  <span className="text-[9px] text-emerald-600 uppercase tracking-wider shrink-0">
                    done
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* ── Result summary ─────────────────────────────────────── */}
        {isDone && result && (
          <div className="pac-glass rounded-lg p-3 space-y-2">
            <div className="text-[10px] text-neutral-500 font-medium uppercase tracking-wider">
              Result
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
              <ResultStat
                label="Scopes created"
                value={result.scopes_created}
                color="text-emerald-400"
              />
              <ResultStat
                label="Outcomes written"
                value={result.outcomes_written}
                color="text-sky-400"
              />
              <ResultStat
                label="Outcomes updated"
                value={result.outcomes_updated ?? 0}
                color="text-amber-400"
              />
              <ResultStat
                label="Outcomes skipped"
                value={result.outcomes_skipped}
                color="text-neutral-500"
              />
            </div>
            {result.outcomes_deleted > 0 && (
              <div className="text-[10px] text-amber-500/80 text-center">
                {result.outcomes_deleted} outcome{result.outcomes_deleted !== 1 ? 's' : ''} deleted
                (overwrite)
              </div>
            )}
            {(result.hot_stream_written ?? 0) > 0 && (
              <div className="text-[10px] text-amber-400/80 text-center">
                + {result.hot_stream_written} hot stream row
                {result.hot_stream_written !== 1 ? 's' : ''}
              </div>
            )}
          </div>
        )}

        {/* ── Error message ──────────────────────────────────────── */}
        {isFailed && (
          <div className="bg-red-950/40 border border-red-900/50 rounded-lg p-3 space-y-1">
            <div className="text-[10px] text-red-400 font-medium uppercase tracking-wider">
              Error
            </div>
            <div className="text-xs text-red-300/90 leading-relaxed break-words">
              {error}
            </div>
          </div>
        )}

        {/* ── Dismiss button (only after done or error) ──────────── */}
        {(isDone || isFailed) && (
          <div className="flex justify-end pt-1">
            <button
              type="button"
              onClick={onDismiss}
              className={
                'px-4 py-1.5 rounded-md text-xs font-medium transition-colors ' +
                (isDone
                  ? 'bg-emerald-600/20 text-emerald-300 border border-emerald-700/50 hover:bg-emerald-600/30'
                  : 'pac-glass-btn text-neutral-300 hover:border-neutral-500')
              }
            >
              {isDone ? 'Done' : 'Dismiss'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Tiny stat widget ────────────────────────────────────────────────────────

function ResultStat({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}) {
  return (
    <div className="pac-glass-subtle rounded px-2 py-1.5">
      <div className="text-[8px] text-neutral-600 uppercase tracking-wider leading-tight">
        {label}
      </div>
      <div className={`text-sm font-semibold tabular-nums ${color}`}>
        {value.toLocaleString()}
      </div>
    </div>
  );
}
